import { PlusIcon, Loader2Icon, SendIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const PRIMARY = 'var(--color-primary-500)';

type FooterMode = 'first' | 'batch' | 'editing';

interface BatchFooterProps {
  mode: FooterMode;
  itemCount: number;
  isSubmitting: boolean;
  isPending: boolean;
  onAddMore: () => void;
  onSubmitSingle: () => void;
  onSubmitBatch: () => void;
  onUpdateItem: () => void;
  onCancelEdit: () => void;
}

export function BatchFooter({
  mode,
  itemCount,
  isSubmitting,
  isPending,
  onAddMore,
  onSubmitSingle,
  onSubmitBatch,
  onUpdateItem,
  onCancelEdit,
}: BatchFooterProps) {
  const { t } = useTranslation();

  const base = 'flex items-center justify-center gap-2 py-2.5 rounded-full font-semibold text-sm cursor-pointer active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed batch-btn-glow';

  // ── Editing mode: Cancel + Update Item ──
  if (mode === 'editing') {
    return (
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onCancelEdit}
          className={`${base} flex-1`}
          style={{
            backgroundColor: 'color-mix(in srgb, var(--color-primary-500) 12%, transparent)',
            color: 'var(--color-primary-700)',
          }}
        >
          {t('quickReport.batch.cancelEdit')}
        </button>
        <button
          type="button"
          onClick={onUpdateItem}
          className={`${base} flex-1 text-white`}
          style={{ backgroundColor: PRIMARY }}
        >
          {t('quickReport.batch.updateItem')}
        </button>
      </div>
    );
  }

  // ── First report: "Add More Reports" + "Submit Report" ──
  if (mode === 'first') {
    return (
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onAddMore}
          disabled={isPending}
          className={`${base} flex-1`}
          style={{
            backgroundColor: 'color-mix(in srgb, var(--color-primary-500) 12%, transparent)',
            color: 'var(--color-primary-700)',
          }}
        >
          <PlusIcon className="h-4 w-4" strokeWidth={3} />
          {t('quickReport.batch.addMore')}
        </button>
        <button
          type="button"
          onClick={onSubmitSingle}
          disabled={isPending}
          className={`${base} flex-1 text-white`}
          style={{ backgroundColor: PRIMARY }}
        >
          {isPending ? (
            <Loader2Icon className="h-4 w-4 animate-spin" />
          ) : (
            <SendIcon className="h-4 w-4" />
          )}
          {t('quickReport.submitReport')}
        </button>
      </div>
    );
  }

  // ── Batch mode: "Add Another" + "Submit N Reports" ──
  return (
    <div className="flex gap-2">
      <button
        type="button"
        onClick={onAddMore}
        disabled={isSubmitting || itemCount >= 20}
        className={base}
        style={{
          paddingInline: '1rem',
          backgroundColor: 'color-mix(in srgb, var(--color-primary-500) 12%, transparent)',
          color: 'var(--color-primary-700)',
        }}
      >
        <PlusIcon className="h-4 w-4" strokeWidth={3} />
        {t('quickReport.batch.addAnother')}
      </button>
      <button
        type="button"
        onClick={onSubmitBatch}
        disabled={isSubmitting}
        className={`${base} flex-1 text-white`}
        style={{ backgroundColor: PRIMARY }}
      >
        <SendIcon className="h-4 w-4" />
        {itemCount === 1
          ? t('quickReport.batch.submitBatchOne')
          : t('quickReport.batch.submitBatch', { count: itemCount })}
      </button>
    </div>
  );
}
